import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '../components/Button';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import { apiClient } from '../api/client';
import { useAppStore } from '../store/useAppStore';
import { Icon } from '../components/Icon';
import { trackLogin, trackSignUp, trackError, setUserId } from '../services/analytics';
import { Config } from '@alveon/shared/config';
import './LoginScreen.css';
import { useTranslation } from 'react-i18next';

interface LoginScreenProps {
  onLoginSuccess: () => void;
}

type AuthMode = 'login' | 'register';

export const LoginScreen: React.FC<LoginScreenProps> = ({ onLoginSuccess }) => {
  const { setCurrentPage } = useAppStore();
  const { t } = useTranslation();
  const [mode, setMode] = useState<AuthMode>('login');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  
  // Анимация печатания текста
  const [displayedText, setDisplayedText] = useState('');
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const cursorOpacity = useRef(1);
  const cursorIntervalRef = useRef<NodeJS.Timeout>();
  const phrases = useMemo(() => [
    t('auth.phrases.voiceFinance'),
    t('auth.phrases.smartCategorization'),
    t('auth.phrases.aiAssistant'),
    t('auth.phrases.expenseControl'),
    t('auth.phrases.allCategories'),
    t('auth.phrases.bankLinking'),
    t('auth.phrases.neverForget'),
    t('auth.phrases.importantNotifications'),
  ], [t]);
  
  // Проверяем был ли удален аккаунт
  const accountDeleted = localStorage.getItem('account_deleted') === 'true';
  const hasTelegramData = !!window.Telegram?.WebApp?.initData;

  // Анимация курсора (мигание)
  useEffect(() => {
    cursorIntervalRef.current = setInterval(() => {
      cursorOpacity.current = cursorOpacity.current === 1 ? 0 : 1;
      // Обновляем DOM напрямую для курсора
      const cursorElement = document.querySelector('.typing-cursor');
      if (cursorElement) {
        (cursorElement as HTMLElement).style.opacity = String(cursorOpacity.current);
      }
    }, 500);
    
    return () => {
      if (cursorIntervalRef.current) {
        clearInterval(cursorIntervalRef.current);
      }
    };
  }, []);

  // Анимация печатания/стирания текста
  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex] || '';
    let timeout: NodeJS.Timeout;

    if (!isDeleting && displayedText.length < currentPhrase.length) {
      // Печатаем текст
      timeout = setTimeout(() => {
        setDisplayedText(currentPhrase.slice(0, displayedText.length + 1));
      }, 40);
    } else if (!isDeleting && displayedText.length === currentPhrase.length) {
      // Пауза после печатания
      timeout = setTimeout(() => {
        setIsDeleting(true);
      }, 1500);
    } else if (isDeleting && displayedText.length > 0) {
      // Стираем текст
      timeout = setTimeout(() => {
        setDisplayedText(currentPhrase.slice(0, displayedText.length - 1));
      }, 10);
    } else if (isDeleting && displayedText.length === 0) {
      // Переходим к следующей фразе
      setIsDeleting(false);
      setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [displayedText, isDeleting, currentPhraseIndex]);

  const handleEmailAuth = (authMode: AuthMode) => {
    setMode(authMode);
    setShowEmailForm(true);
    setError('');
  };

  const handleCloseEmailForm = () => {
    setShowEmailForm(false);
    setError('');
    setPassword('');
    setConfirmPassword('');
    setEmail('');
    setConfirmEmail('');
    setFirstName('');
  };

  // Функция для определения надежности пароля
  const getPasswordStrength = () => {
    if (!password) return { strength: 0, label: '', color: '#E0E0E0' };
    
    let strength = 0;
    const checks = {
      length: password.length >= 8,
      long: password.length >= 12,
      hasUpper: /[A-ZА-Я]/.test(password),
      hasLower: /[a-zа-я]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    if (checks.length) strength += 20;
    if (checks.long) strength += 20;
    if (checks.hasUpper) strength += 15;
    if (checks.hasLower) strength += 15;
    if (checks.hasNumber) strength += 15;
    if (checks.hasSpecial) strength += 15;

    if (strength >= 80) {
      return { strength: 100, label: t('auth.passwordStrength.excellent'), color: '#34C759' };
    } else if (strength >= 50) {
      return { strength: 66, label: t('auth.passwordStrength.good'), color: '#FF9500' };
    } else if (strength >= 20) {
      return { strength: 33, label: t('auth.passwordStrength.weak'), color: '#FF3B30' };
    } else {
      return { strength: 0, label: t('auth.passwordStrength.veryWeak'), color: '#FF3B30' };
    }
  };

  const passwordStrength = getPasswordStrength();

  const handleLogin = async () => {
    setError('');
    const loginIdentifier = userId.trim() || email.toLowerCase().trim();
    
    if (!loginIdentifier || !password) {
      setError(t('auth.fillAllFields'));
      return;
    }

    setIsProcessing(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || Config.API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: loginIdentifier, password }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: t('auth.invalidCredentials') }));
        throw new Error(errorData.error || t('auth.invalidEmailOrPassword'));
      }
      
      const data = await response.json();
      
      if (data.token) {
        apiClient.setToken(data.token);
        if (data.refresh_token) apiClient.setRefreshToken(data.refresh_token);
        localStorage.setItem('auth_token', data.token);
        if (data.refresh_token) localStorage.setItem('refresh_token', data.refresh_token);
        localStorage.setItem('user_id', String(data.user_id));
        
        console.log('✅ Login successful');
        
        // Отслеживаем успешный логин
        trackLogin('email');
        setUserId(data.user_id);
        
        handleCloseEmailForm();
        onLoginSuccess();
      } else {
        throw new Error(t('auth.serverNoToken'));
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.message || t('auth.invalidCredentials'));
      trackError('login_failed', error.message || 'Unknown error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRegister = async () => {
    setError('');
    
    if (!firstName || !email || !confirmEmail || !password || !confirmPassword) {
      setError(t('auth.fillAllFields'));
      return;
    }

    if (email !== confirmEmail) {
      setError(t('auth.emailsDoNotMatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    setIsProcessing(true);
    try {
      const data = await apiClient.register(firstName.trim(), email.toLowerCase().trim(), password);
      
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('user_id', String(data.user_id));
        
        console.log('✅ Registration successful');
        
        // Отслеживаем успешную регистрацию
        trackSignUp('email');
        setUserId(data.user_id);
        
        handleCloseEmailForm();
        onLoginSuccess();
      } else {
      throw new Error(t('auth.serverNoToken'));
      }
    } catch (error: any) {
      console.error('Registration error:', error);
    setError(error.message || t('auth.registrationError'));
    trackError('registration_failed', error.message || t('common.error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsProcessing(true);
    setError('');
    
    try {
      const { auth_url } = await apiClient.initiateGoogleAuth(false);
      
      if (!auth_url) {
        throw new Error(t('auth.googleLoginFailed'));
      }

      console.log('✅ Google OAuth URL получен:', auth_url);
      
      // Открываем OAuth в новом окне или редиректим
      window.location.href = auth_url;
    } catch (error: any) {
      console.error('Google OAuth error:', error);
    setError(error.message || t('auth.googleLoginFailed'));
      setIsProcessing(false);
    }
  };

  const handleAppleLogin = async () => {
    // Apple Sign In для веб-версии пока не реализован
    // Используйте мобильное приложение для входа через Apple
  setError(t('auth.appleLoginFailed'));
  };

  // Если аккаунт был удален и есть Telegram данные - показываем специальное сообщение
  if (accountDeleted && hasTelegramData) {
    return (
      <div className="login-screen">
        <div className="login-container">
          <div className="login-header">
            <div className="login-logo">
              <Icon name="alert-circle" size={60} color="#FF9500" />
            </div>
            <h1 className="login-title">{t('welcome.accountDeleted')}</h1>
            <p className="login-subtitle">
              {t('welcome.accountDeletedSubtitle')}
            </p>
          </div>

          <div className="login-actions">
            <Button
              variant="primary"
              onClick={async () => {
                setIsProcessing(true);
                try {
                  localStorage.removeItem('account_deleted');
                  const authResponse = await apiClient.validateAndGetToken();
                  if (authResponse.status === 'ok') {
                    onLoginSuccess();
                  }
                } catch (error: any) {
                  setError(error.message || t('auth.registrationError'));
                } finally {
                  setIsProcessing(false);
                }
              }}
              fullWidth
              loading={isProcessing}
              disabled={isProcessing}
            >
              {t('welcome.createNewAccount')}
            </Button>
            
            <Button
              variant="secondary"
              onClick={() => {
                localStorage.removeItem('account_deleted');
                window.location.reload();
              }}
              fullWidth
              disabled={isProcessing}
            >
              {t('welcome.loginAnotherWay')}
            </Button>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Определяем, поддерживается ли Apple Sign In (Safari на iOS/macOS)
  const isAppleSignInSupported = 
    /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
    /Mac/.test(navigator.userAgent);

  return (
    <div className="login-screen">
      <div className="login-container">
        <div className="login-logo-container">
          <img 
            src="/android-chrome-512x512.png" 
            alt="Alveon Logo"
            className="login-logo-image"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              if (target.src.includes('android-chrome-512x512.png')) {
                target.src = '/android-chrome-192x192.png';
              } else {
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent && !parent.querySelector('.login-logo-fallback')) {
                  const fallback = document.createElement('div');
                  fallback.className = 'login-logo-fallback';
                  fallback.innerHTML = `
                    <svg width="160" height="160" viewBox="0 0 440.000000 440.000000" preserveAspectRatio="xMidYMid meet">
                      <g transform="translate(0.000000,440.000000) scale(0.100000,-0.100000)" fill="#2d3748">
                        <path d="M2122 3569 c-65 -12 -144 -52 -183 -91 -27 -27 -297 -419 -319 -463 -3 -5 -86 -131 -184 -280 -216 -326 -363 -549 -441 -670 -32 -49 -77 -119 -100 -155 -134 -206 -225 -363 -240 -416 -25 -82 -24 -221 3 -295 36 -102 101 -186 202 -264 73 -56 156 -90 257 -106 71 -11 92 -10 169 5 94 19 213 69 264 112 53 45 137 159 370 503 l43 64 96 -5 c167 -8 207 -37 341 -246 47 -73 102 -158 122 -190 20 -31 50 -80 67 -108 41 -69 87 -112 130 -124 47 -13 546 -13 636 0 92 14 181 63 267 148 121 119 172 270 148 440 -11 82 -26 110 -205 382 -67 102 -184 284 -260 405 -76 121 -146 231 -155 245 -29 44 -82 127 -315 495 -297 469 -307 485 -346 521 -93 85 -231 120 -367 93z"/>
                      </g>
                    </svg>
                  `;
                  parent.insertBefore(fallback, target);
                }
              }
            }}
          />
          <h1 className="login-logo-text">Alveon</h1>
        </div>
        
        <div className="login-typing-container">
          <p className="login-typing-text">
            {displayedText}
            <span className="typing-cursor">|</span>
          </p>
        </div>

        <div className="login-bottom-content">
          {error && !showEmailForm && (
            <div className="login-error-container">
              <Icon name="alert-circle" size={20} color="#FF3B30" />
              <span className="login-error-text">{error}</span>
            </div>
          )}

          {/* Кнопка Apple (белая) - только на поддерживаемых устройствах */}
          {isAppleSignInSupported && (
            <button
              onClick={handleAppleLogin}
              disabled={isProcessing}
              className="login-auth-button login-auth-button--apple"
            >
              <svg className="login-auth-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span className="login-auth-button-text">{t('auth.continueWithApple')}</span>
            </button>
          )}

          {/* Кнопка Google (темно-серая) */}
          <button
            onClick={handleGoogleLogin}
            disabled={isProcessing}
            className="login-auth-button login-auth-button--google"
          >
            <svg className="login-auth-icon" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span className="login-auth-button-text">{t('auth.continueWithGoogle')}</span>
          </button>

          {/* Кнопка Зарегистрироваться (темно-серая) */}
          <button
            onClick={() => handleEmailAuth('register')}
            disabled={isProcessing}
            className="login-auth-button login-auth-button--register"
          >
            <span className="login-auth-button-text">{t('auth.register')}</span>
          </button>

          {/* Кнопка Войти (черная с белой рамкой) */}
          <button
            onClick={() => handleEmailAuth('login')}
            disabled={isProcessing}
            className="login-auth-button login-auth-button--login"
          >
            <span className="login-auth-button-text">{t('auth.login')}</span>
          </button>
        </div>
      </div>

      {/* Модальное окно с формой входа/регистрации */}
      {showEmailForm && (
        <div className="login-modal-overlay" onClick={handleCloseEmailForm}>
          <div className="login-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="login-modal-header">
              <h2 className="login-modal-title">
                {mode === 'login' ? t('auth.login') : t('auth.register')}
              </h2>
              <button className="login-modal-close" onClick={handleCloseEmailForm}>
                <Icon name="close" size={24} color="#FFFFFF" />
              </button>
            </div>

            <div className="login-modal-body">
              {error && (
                <div className="login-error-container">
                  <Icon name="alert-circle" size={20} color="#FF3B30" />
                  <span className="login-error-text">{error}</span>
                </div>
              )}

              {mode === 'register' && (
                <div className="login-form-group">
                  <label className="login-form-label">{t('auth.firstName')}</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t('auth.firstName')}
                    className="login-form-input"
                    disabled={isProcessing}
                    autoCapitalize="words"
                  />
                </div>
              )}

              {mode === 'login' ? (
                <div className="login-form-group">
                  <label className="login-form-label">{t('auth.email')}</label>
                  <input
                    type="text"
                    value={userId || email}
                    onChange={(e) => {
                      setUserId(e.target.value);
                      if (e.target.value) setEmail('');
                      if (error) setError('');
                    }}
                    placeholder={t('auth.email')}
                    className="login-form-input"
                    disabled={isProcessing}
                    autoCapitalize="none"
                  />
                </div>
              ) : (
                <>
                  <div className="login-form-group">
                    <label className="login-form-label">{t('auth.email')}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="example@email.com"
                      className="login-form-input"
                      disabled={isProcessing}
                      autoCapitalize="none"
                      autoComplete="email"
                    />
                  </div>
                  <div className="login-form-group">
                    <label className="login-form-label">{t('auth.confirmEmail')}</label>
                    <input
                      type="email"
                      value={confirmEmail}
                      onChange={(e) => setConfirmEmail(e.target.value)}
                      placeholder={t('auth.confirmEmail')}
                      className="login-form-input"
                      disabled={isProcessing}
                      autoCapitalize="none"
                      autoComplete="email"
                    />
                  </div>
                </>
              )}

              <div className="login-form-group">
                <label className="login-form-label">{t('auth.password')}</label>
                <div className="login-password-container">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError('');
                    }}
                    placeholder={mode === 'register' ? t('auth.passwordMinLength') : t('auth.password')}
                    className="login-password-input"
                    disabled={isProcessing}
                    autoCapitalize="none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="login-password-toggle"
                  >
                    <Icon name={showPassword ? 'eye-off' : 'eye'} size={20} color="#FFFFFF" />
                  </button>
                </div>
                {mode === 'register' && password.length > 0 && (
                  <div className="login-password-strength">
                    <div className="login-password-strength-bar">
                      <div 
                        className="login-password-strength-fill"
                        style={{ 
                          width: `${passwordStrength.strength}%`,
                          backgroundColor: passwordStrength.color,
                        }}
                      />
                    </div>
                    <span 
                      className="login-password-strength-text"
                      style={{ color: passwordStrength.color }}
                    >
                      {passwordStrength.label}
                    </span>
                  </div>
                )}
              </div>

              {mode === 'register' && (
                <div className="login-form-group">
                  <label className="login-form-label">{t('auth.confirmPassword')}</label>
                  <div className="login-password-container">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t('auth.confirmPasswordPlaceholder')}
                      className="login-password-input"
                      disabled={isProcessing}
                      autoCapitalize="none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="login-password-toggle"
                    >
                      <Icon name={showConfirmPassword ? 'eye-off' : 'eye'} size={20} color="#FFFFFF" />
                    </button>
                  </div>
                  {confirmPassword.length > 0 && (
                    <div className="login-password-match">
                      <Icon
                        name={password === confirmPassword ? 'check-circle' : 'alert-circle'}
                        size={16}
                        color={password === confirmPassword ? '#34C759' : '#FF3B30'}
                      />
                      <span
                        className="login-password-match-text"
                        style={{ 
                          color: password === confirmPassword ? '#34C759' : '#FF3B30' 
                        }}
                      >
                        {password === confirmPassword ? t('auth.passwordMatch') : t('auth.passwordMismatch')}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={mode === 'login' ? handleLogin : handleRegister}
                disabled={isProcessing}
                className="login-submit-button"
              >
                {isProcessing ? (
                  <span className="login-submit-spinner">{t('common.loading')}</span>
                ) : (
                  mode === 'login' ? t('auth.login') : t('auth.register')
                )}
              </button>

              <div className="login-switch-mode">
                <span className="login-switch-mode-text">
                  {mode === 'login' ? t('auth.noAccount') + ' ' : t('auth.haveAccount') + ' '}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setMode(mode === 'login' ? 'register' : 'login');
                    setError('');
                  }}
                  className="login-switch-mode-link"
                >
                  {mode === 'login' ? t('auth.register') : t('auth.login')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
